/** @jsx jsx */
import PropTypes from 'prop-types';
import { jsx } from '@emotion/core';
import { withNamespaces } from 'react-i18next';
import { Text, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import defaultsDeep from 'lodash/defaultsDeep';
import compose from 'lodash/fp/compose';
import Swiper from 'react-id-swiper';
import Picture from '../../../ui/Picture';
import { modalIds, ModalsConsumer } from '../../../../contexts/ModalsContext';
import { SafePureComponent } from '../../../../utils/SafePureComponent';
import { GridConfig } from '../../../../styles/Grid/GridSizes';
import { formatPriceToCurrencyString } from '../../../ui/PriceGenerator';
import {
  IMAGE_CONFIG_VALUE,
  CLOUDINARY_BASE_VALUES,
} from '../../../../utils/constants';
import { withConsumer } from '../../../../contexts';
import { Breakpoints } from '../../../../styles/Grid/GridSizes';
import * as dicts from '../../../../utils/dictionarykeys';
import * as styles from './index.styles.js';

const imageSizes = {
  [Breakpoints.xl]: {
    ...CLOUDINARY_BASE_VALUES,
    aspectRatio: '4:3',
    width: 404,
    height: 306,
  },
  [Breakpoints.lg]: {
    ...CLOUDINARY_BASE_VALUES,
    aspectRatio: '4:3',
    width: 460,
    height: 400,
  },
  [Breakpoints.md]: {
    ...CLOUDINARY_BASE_VALUES,
    aspectRatio: '4:3',
    width: 546,
    height: 341,
  },
  [Breakpoints.sm]: {
    ...CLOUDINARY_BASE_VALUES,
    aspectRatio: '4:3',
    width: 311,
    height: 220,
  },
};

const getSlideImage = protonId => {
  return {
    value: {
      src: `https://assets.iwgplc.com/image/upload/${IMAGE_CONFIG_VALUE}/CentreImagery/${protonId}/${protonId}_5.jpg`,
    },
  };
};

const fallbackImage = {
  value: {
    src: '/-/media/Regus/Settings/Fallbacks/locations_5.ashx',
  },
};

const defaultData = {
  Context: {
    relatedLocation: '',
  },
  NearbyLocationsTitle: {
    value: '',
  },
};

// Slide starts in 1 (second slide) to avoid an issue with Picture loading image and
// loop: true. The issue happens when the slide with an image is the first one in the
// swiper implementation.
export const baseSwiperParams = {
  loop: true,
  slidesPerView: 'auto',
  initialSlide: 1,
  spaceBetween: GridConfig.lg.gutter,
  navigation: {
    nextEl: '.swiper-button-next',
  },
};

class Locations extends SafePureComponent {
  fields = defaultsDeep(this.props.fields, defaultData);

  renderLocationSlide(location, id) {
    const { t, modal, currency } = this.props;
    const {
      ProtonId,
      LocationName,
      Price,
      Url,
      LocationBrandImage,
      IsPartner,
    } = location;
    const brandImage = LocationBrandImage ? JSON.parse(LocationBrandImage) : '';

    return (
      <div className="swiper-slide" css={styles.slide} key={`location-${id}`}>
        <a
          href={Url}
          target="_blank"
          rel="noopener noreferrer"
          css={styles.locationInfo}
        >
          <Picture
            image={getSlideImage(ProtonId)}
            fallBack={fallbackImage}
            imageSizes={imageSizes}
            instantLoad
            isClientLink
            imageCss={styles.slideImage}
            pictureCss={styles.slidePicture}
          />
          <div css={styles.slideText}>
            <p css={styles.address}>{LocationName}</p>
            <p css={styles.price}>
              {t(currency)}
              {formatPriceToCurrencyString(Price)}/
              {t(dicts.SOLUTION_BUILDER.MONTHTEXT)}*
            </p>
          </div>
        </a>
        <div css={styles.slideFooter} className={'slide-locations-footer'}>
          <div css={styles.bookTourContainer}>
            <button
              css={styles.bookTourCta}
              onClick={() => modal.openModal(modalIds.bookTour, location)}
            >
              {t(dicts.CTAS.BOOK_A_TOUR_CTA)}
            </button>
          </div>
          {Boolean(brandImage) && (
            <div css={styles.slideFooterBrand}>
              <div css={styles.brandImageContainer}>
                <Picture
                  image={brandImage}
                  imageCss={styles.brandImage}
                  pictureCss={styles.brandImagePicture}
                  instantLoad
                />
              </div>
              {IsPartner && (
                <span css={styles.partnerText}>{t('AssociatedPartner')}</span>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  renderNonLocationSlide() {
    const { fields, props } = this;
    const { t } = props;
    const { NearbyLocationsTitle, RelatedLocationsLink } = fields;

    return (
      <div css={styles.slide} className="swiper-slide" key="non-location">
        <a
          href={RelatedLocationsLink}
          css={styles.nonLocation}
          target="_blank"
          rel="noopener noreferrer"
        >
          <i css={styles.nonLocationIcon} className="icon-all-locations" />
          <p css={styles.nonLocationDescription}>
            <Text field={NearbyLocationsTitle} />
          </p>
          <p css={styles.nonLocationCta}>
            {t(dicts.CTAS.SEE_ALL_LOCATIONS_CTA)}
          </p>
        </a>
      </div>
    );
  }

  safeRender() {
    const { locationsPrices } = this.props;
    const locations = [
      locationsPrices.min,
      locationsPrices.mid,
      locationsPrices.max,
    ].filter(Boolean);

    if (locations.length === 0) return null;

    // Needs to calculate loopedSlides based on number of elements to avoid slide duplicate issue
    // with Picture.
    const swiperParams = {
      ...baseSwiperParams,
      loopedSlides: locations.length,
    };

    return (
      <div css={styles.locations}>
        <Swiper {...swiperParams}>
          {/* Non-location slide must be first due the issue with picture and loop: true */}
          {this.renderNonLocationSlide()}
          {locations.map((location, index) => {
            return this.renderLocationSlide(location, index);
          })}
        </Swiper>
      </div>
    );
  }
}

Locations.displayName = 'Locations';

Locations.defaultProps = {
  fields: defaultData,
  t: key => key,
};

Locations.propTypes = {
  fields: PropTypes.shape({
    Context: PropTypes.shape({
      relatedLocation: PropTypes.string,
    }),
    Locations: PropTypes.arrayOf(
      PropTypes.shape({ address: PropTypes.string, price: PropTypes.string }),
    ),
  }),
  modal: PropTypes.any,
  locationsPrices: PropTypes.any,
  currency: PropTypes.string,
  t: PropTypes.func,
};

export default compose(
  withConsumer(ModalsConsumer, 'modal'),
  withSitecoreContext(),
  withNamespaces(),
)(Locations);
